/**
 * @file Layout.js — Shell authentifié avec barre de navigation supérieure, sous-navigation,
 * sélecteur de cercle, commutateur de mode de vue et menu utilisateur.
 * Intègre le ThemeToggle (R-0024), CommandPalette (R-0088) et Breadcrumbs (R-0089).
 */
import { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { User, LogOut, CircleUser, UserRound, ShieldCheck, BriefcaseBusiness, Database } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import CommandPalette from './CommandPalette';
import Breadcrumbs from './Breadcrumbs';
import { APP_ROUTES, getDefaultRouteForView, getSectionViewForPath } from '../lib/routes';

/** Libellé d'affichage et icône pour chaque mode de vue. */
const viewMeta = {
  user: {
    labelKey: 'nav.user',
    shortLabelKey: 'nav.user_short',
    descriptionKey: 'nav.user_description',
    icon: UserRound,
  },
  admin: {
    labelKey: 'nav.admin',
    shortLabelKey: 'nav.admin_short',
    descriptionKey: 'nav.admin_description',
    icon: ShieldCheck,
  },
  site: {
    labelKey: 'nav.site_manager',
    shortLabelKey: 'nav.site_manager_short',
    descriptionKey: 'nav.site_manager_description',
    icon: BriefcaseBusiness,
  },
};

/** Liens de sous-navigation pour chaque mode de vue. */
const navLinks = {
  user: [
    { to: APP_ROUTES.dashboard, labelKey: 'nav.dashboard', testId: 'dashboard' },
    { to: APP_ROUTES.circles, labelKey: 'nav.my_circles', testId: 'my-circles' },
    { to: APP_ROUTES.events, labelKey: 'nav.events', testId: 'events' },
  ],
  admin: [
    { to: APP_ROUTES.admin, labelKey: 'nav.overview', testId: 'overview' },
    { to: APP_ROUTES.adminMembers, labelKey: 'nav.members', testId: 'members' },
    { to: APP_ROUTES.adminInvites, labelKey: 'nav.invites', testId: 'invites' },
    { to: APP_ROUTES.adminEvents, labelKey: 'nav.events', testId: 'events' },
  ],
  site: [
    { to: APP_ROUTES.site, labelKey: 'nav.overview', testId: 'overview' },
    { to: APP_ROUTES.siteCircles, labelKey: 'nav.circles', testId: 'circles' },
    { to: APP_ROUTES.siteDatabase, labelKey: 'nav.database', testId: 'database' },
  ],
};

/**
 * Composant principal du layout authentifié.
 * Affiche la barre supérieure (logo, commutateur de rôle, sélecteur de cercle, menu utilisateur),
 * une barre de sous-navigation et le contenu de la page.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Contenu de la page à afficher sous la navigation.
 * @returns {React.ReactElement}
 */
export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const {
    currentView, setCurrentView, availableViews,
    circles, adminCircles, eventAdminCircles, adminViewCircles, activeCircle, setActiveCircle,
  } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const routeView = getSectionViewForPath(location.pathname);
  const effectiveView = availableViews.includes(currentView) ? currentView : 'user';
  const canAccessSiteView = availableViews.includes('site');
  const showQuickDatabaseLink = canAccessSiteView && effectiveView === 'site';
  const selectedViewMeta = viewMeta[effectiveView] || viewMeta.user;
  const SelectedViewIcon = selectedViewMeta.icon;

  // En vue admin, les gestionnaires de site voient tous les cercles ; les admins de cercle ne voient que les leurs
  const selectorCircles = effectiveView === 'admin'
    ? adminViewCircles
    : circles;
  const showSelector = selectorCircles.length > 0 && effectiveView !== 'site';
  const canManageCircleSettings = adminCircles.length > 0;
  const canManageAnyEvents = eventAdminCircles.length > 0;

  useEffect(() => {
    if (!routeView || routeView === effectiveView) return;

    if (availableViews.includes(routeView)) {
      setCurrentView(routeView);
      return;
    }

    navigate(getDefaultRouteForView(effectiveView), { replace: true });
  }, [availableViews, effectiveView, navigate, routeView, setCurrentView]);

  /** Change le mode de vue et navigue vers sa route par défaut. */
  const handleViewChange = (view) => {
    setCurrentView(view);
    navigate(getDefaultRouteForView(view));
  };

  const handleShortcutViewChange = (view, path = getDefaultRouteForView(view)) => {
    setCurrentView(view);
    navigate(path);
  };

  const links = (effectiveView === 'admin'
    ? navLinks.admin.filter(link => {
      if (link.to === APP_ROUTES.adminEvents) return canManageAnyEvents;
      if (link.to === APP_ROUTES.adminMembers || link.to === APP_ROUTES.adminInvites) return canManageCircleSettings;
      return true;
    })
    : navLinks[effectiveView] || navLinks.user);
  const appName = t('brand.app_name');
  const buildMeta = t('layout.build_meta', {
    app_name: appName,
    version: process.env.REACT_APP_VERSION || '–',
    build_id: process.env.REACT_APP_BUILD_ID || '–',
    deploy_ts: process.env.REACT_APP_DEPLOY_TS || '–',
  });

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="app-layout">
      {/* ── Palette de commandes (Ctrl+K / Cmd+K) ── */}
      <CommandPalette />

      {/* ── Barre supérieure ── */}
      <nav className="sticky top-0 z-50 bg-background border-b border-border" data-testid="top-navbar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="text-lg font-semibold tracking-tight text-foreground" data-testid="app-logo" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {appName}
            </Link>

            <div className="flex items-center gap-2">
              {/* Sélecteur de cercle */}
              {showSelector && (
                <Select
                  value={activeCircle?.id || ''}
                  onValueChange={(val) => setActiveCircle(val)}
                >
                  <SelectTrigger className={`w-[160px] sm:w-[200px] rounded-sm text-xs h-8 ${activeCircle ? 'border-foreground/20 shadow-[0_0_0_1px_rgba(17,24,39,0.06)]' : 'border-border'}`} data-testid="circle-selector">
                    <SelectValue placeholder={t('nav.select_circle')} />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm shadow-none border-border">
                    {selectorCircles.map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-xs" data-testid={`circle-option-${c.slug || c.id}`}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {availableViews.includes('admin') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-8 text-xs gap-1.5"
                  data-testid="quick-admin-link"
                  onClick={() => handleShortcutViewChange('admin')}
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span className="hidden lg:inline">{t('nav.admin')}</span>
                </Button>
              )}

              {showQuickDatabaseLink && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-8 text-xs gap-1.5"
                  data-testid="quick-database-link"
                  onClick={() => handleShortcutViewChange('site', APP_ROUTES.siteDatabase)}
                >
                  <Database className="h-3.5 w-3.5" />
                  <span className="hidden lg:inline">{t('nav.database')}</span>
                </Button>
              )}

              {/* Menu déroulant des rôles */}
              {availableViews.length > 1 && (
                <Select value={effectiveView} onValueChange={handleViewChange}>
                  <SelectTrigger
                    className="h-8 w-[168px] sm:w-[212px] rounded-sm border-border text-xs shrink-0"
                    data-testid="role-view-switcher"
                    aria-label={t('nav.available_statuses')}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <SelectedViewIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate" data-testid={`role-view-current-${effectiveView}`}>
                        {t(selectedViewMeta.labelKey)}
                      </span>
                    </span>
                  </SelectTrigger>
                  <SelectContent className="rounded-sm border-border shadow-none">
                    {availableViews.map(v => {
                      const Icon = viewMeta[v].icon;
                      const label = t(viewMeta[v].labelKey);
                      const description = t(viewMeta[v].descriptionKey);
                      return (
                        <SelectItem
                          key={v}
                          value={v}
                          className="text-xs"
                          data-testid={`role-view-${v}`}
                        >
                          <div className="flex items-center gap-2 pr-4">
                            <Icon className="h-3.5 w-3.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="truncate">{label}</p>
                              <p className="truncate text-[10px] text-muted-foreground">{description}</p>
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}

              {/* Bouton dark/light mode (R-0024) */}
              <ThemeToggle />

              {/* Menu utilisateur */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs gap-1.5" data-testid="user-menu-trigger">
                    <CircleUser className="h-4 w-4" />
                    <span className="hidden sm:inline">{user?.first_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-sm shadow-none border-border min-w-[140px]">
                  <DropdownMenuItem onClick={() => navigate(APP_ROUTES.profile)} className="text-xs gap-2" data-testid="profile-menu-item">
                    <User className="h-3.5 w-3.5" /> {t('nav.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-xs gap-2 text-destructive focus:text-destructive" data-testid="logout-menu-item">
                    <LogOut className="h-3.5 w-3.5" /> {t('nav.sign_out')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Sous-navigation ── */}
      <div className="border-b border-border/50 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-6 h-10 items-end">
            {links.map(link => {
              const active = location.pathname === link.to || (link.to !== '/' && location.pathname.startsWith(link.to + '/'));
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-xs font-medium pb-2.5 border-b-2 transition-colors ${
                    active ? 'text-foreground border-foreground' : 'text-muted-foreground border-transparent hover:text-foreground/70'
                  }`}
                  data-testid={`nav-link-${link.testId}`}
                >
                  {t(link.labelKey)}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Fil d'Ariane (R-0089) ── */}
      <Breadcrumbs />

      {/* ── Contenu ── */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
        {children}
      </main>

      {/* ── Pied de page (R-0078) ── */}
      <footer className="border-t border-border/50 py-4 text-center text-[10px] text-muted-foreground space-y-1">
        <div className="flex items-center justify-center gap-3">
          <span>{buildMeta}</span>
          <span>·</span>
          <button
            onClick={() => i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')}
            className="hover:text-foreground transition-colors underline"
            data-testid="lang-toggle"
          >
            {i18n.language === 'fr' ? 'EN' : 'FR'}
          </button>
        </div>
        <div>{t('brand.legal_notice')}</div>
      </footer>
    </div>
  );
}
